"use client";

// Modal Context for managing modals outside smooth-wrapper
import { createContext, useContext, useState, ReactNode } from "react";
import { createPortal } from "react-dom";

interface ModalContextType {
  openModal: (content: ReactNode) => void;
  closeModal: () => void;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error("useModal must be used within a ModalProvider");
  }
  return context;
};

export const ModalProvider = ({ children }: { children: ReactNode }) => {
  const [modalContent, setModalContent] = useState<ReactNode | null>(null);

  const openModal = (content: ReactNode) => {
    setModalContent(content);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setModalContent(null);
    document.body.style.overflow = "unset";
  };

  return (
    <ModalContext.Provider value={{ openModal, closeModal }}>
      {children}
      {modalContent &&
        createPortal(
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-[9999]">
            {modalContent}
          </div>,
          document.body
        )}
    </ModalContext.Provider>
  );
};
